/* eslint-disable tailwindcss/no-arbitrary-value */
import { Modal, ModalBody, ModalHeader } from '@one-thd/sui-atomic-components';
import { ConfirmedFilled } from '@one-thd/sui-icons';
import React, { useState } from 'react';
import { GetQBOrderCount } from '../../../../hooks/purchase-history-hooks';
import { IAlert } from '../../../../models/common';
import { IPHRequest } from '../../../../models/purchase-history';
import { TQBRenderFlag } from '../../../../models/purchase-history/quick-books-export';
import { getWindow } from '../../../../utils/general';
import QBDesktopExportWalkthrough from './QBDesktopExportWalkthrough';
import QuickBooksDesktopExport from './QuickBooksDesktopExport';
import QuickBooksExportButton from './QuickBooksExportButton';
import QuickBooksOnlineExport from './QuickBooksOnlineExport';

export interface IQuickBooksModal {
  openModal: boolean;
  setOpenModal: (value: boolean) => void;
  setAlertState: (value: IAlert | null) => void;
  request: IPHRequest;
}

const qbFeatures = {
  online: [
    'QuickBooks Simple',
    'QuickBooks Essential',
    'QuickBooks Plus',
    'QuickBooks Advanced',
  ],
  desktop: [
    'QuickBooks Enterprise',
    'QuickBooks Accountant',
    'QuickBooks Premier',
    'QuickBooks Pro',
  ],
};

const getModalTitle = {
  desktop: 'QuickBooks Desktop Export',
  online: 'QuickBooks Online Export',
  walkthrough: 'Export Desktop Walkthrough',
};

const renderSectionTitle = (title: string) => {
  const applyInverseBorder: string =
    title === 'Online' ? 'sui-border-r-primary' : 'sui-border-l-primary';
  return (
    <div
      className={`sui-flex sui-justify-center sui-items-center sui-bg-[#5fac47] sui-text-inverse sui-mb-6 sui-min-w-full sui-h-8 sui-text-xl ${applyInverseBorder}`}
    >
      {title}
    </div>
  );
};

const featureList = (features: string[]) => {
  return (
    <div
      className="sui-mx-2 sui-border-t-1 sui-border-strong sui-border-solid"
      data-testid="quickbooks-selector-feature-list"
    >
      {features.map((feature, index) => (
        <div
          key={index}
          className="sui-flex sui-items-center sui-justify-start sui-text-xl sui-pl-5 sui-pb-1 sui-gap-1 first:sui-mt-6"
        >
          <div className="sui-fill-[#5fac47] sui-pb-1">
            <ConfirmedFilled color="inherit" />
          </div>
          <div data-testid="quickbooks-selector-feature">{feature}</div>
        </div>
      ))}
    </div>
  );
};

const onlineDesktopSelectorElement = (
  setQBRenderFlag: (value: TQBRenderFlag) => void,
  qbCount: number | undefined,
  setOpenModal: (value: boolean) => void,
  setAlertState: (value: IAlert | null) => void,
) => {
  const screenWidth = getWindow()?.screen?.width;
  return (
    <ModalBody>
      <div className="sui-grid sui-grid-cols-2 sui-justify-center sui-relative">
        <div
          style={{
            position: 'absolute',
            top: '15%',
            left: '50%',
            transform: 'translate(-50%, -20%)',
          }}
        >
          <img
            src="https://assets.thdstatic.com/images/v1/OrSymbol.svg"
            alt="Or Symbol"
            width="56px"
            height="56px"
          />
        </div>
        <div className="sui-col-span-1 sui-border-solid sui-border-strong sui-border-1 ">
          {renderSectionTitle('Online')}
          <div className="sui-flex sui-align-center sui-justify-center sui-mb-4">
            <img
              src="https://assets.thdstatic.com/images/v1/OnlineTitle.svg"
              alt="Online Title"
              width="80%"
              height="78px"
              style={{
                paddingBottom:
                  screenWidth && screenWidth > 1440 ? '10px' : '7px',
              }}
              className="sui-mt-2"
            />
          </div>
          {featureList(qbFeatures.online)}
          <QuickBooksExportButton
            isDesktopExport={false}
            setQBRenderFlag={setQBRenderFlag}
            setOpenModal={setOpenModal}
            setAlertState={setAlertState}
            qbCount={qbCount}
          />
        </div>

        <div className="sui-col-span-1 sui-border-solid sui-border-strong sui-border-1">
          {renderSectionTitle('Desktop')}
          <div className="sui-flex sui-align-center sui-justify-center sui-mb-3">
            <img
              src="https://assets.thdstatic.com/images/v1/DesktopTitle.svg"
              alt="Desktop Title"
              width="80%"
              height="78px"
              className="sui-mt-2"
            />
          </div>
          {featureList(qbFeatures.desktop)}
          <QuickBooksExportButton
            isDesktopExport
            setQBRenderFlag={setQBRenderFlag}
            setOpenModal={setOpenModal}
            setAlertState={setAlertState}
          />
        </div>
      </div>
    </ModalBody>
  );
};

const modalBodyRenderLogic = (
  qbRenderFlag: TQBRenderFlag,
  setQBRenderFlag: (value: TQBRenderFlag) => void,
  qbCount: number | undefined,
  setOpenModal: (value: boolean) => void,
  setAlertState: (value: IAlert | null) => void,
  request: IPHRequest,
) => {
  if (qbRenderFlag === 'online') {
    return (
      <QuickBooksOnlineExport
        setQBRenderFlag={setQBRenderFlag}
        request={request}
        setOpenModal={setOpenModal}
        setAlertState={setAlertState}
      />
    );
  }
  if (qbRenderFlag === 'desktop') {
    return (
      <QuickBooksDesktopExport
        setQBRenderFlag={setQBRenderFlag}
        request={request}
        setOpenModal={setOpenModal}
        setAlertState={setAlertState}
      />
    );
  }
  if (qbRenderFlag === 'walkthrough') {
    return <QBDesktopExportWalkthrough setQBRenderFlag={setQBRenderFlag} />;
  }
  return onlineDesktopSelectorElement(
    setQBRenderFlag,
    qbCount,
    setOpenModal,
    setAlertState,
  );
};

const QuickBooksModal = ({
  openModal,
  setOpenModal,
  request,
  setAlertState,
}: IQuickBooksModal) => {
  const [qbRenderFlag, setQBRenderFlag] = useState<TQBRenderFlag>();

  const qbCount = GetQBOrderCount(request, openModal);

  const handleCloseAndReset = () => {
    setOpenModal(false);
    setTimeout(() => setQBRenderFlag(undefined), 1000);
  };

  return (
    <Modal
      open={openModal}
      onClose={handleCloseAndReset}
      size={qbRenderFlag === undefined ? 'lg' : 'md'}
    >
      <ModalHeader onClose={handleCloseAndReset}>
        {qbRenderFlag
          ? getModalTitle[qbRenderFlag]
          : 'Quickbooks Export Selection'}
      </ModalHeader>
      {modalBodyRenderLogic(
        qbRenderFlag,
        setQBRenderFlag,
        qbCount,
        setOpenModal,
        setAlertState,
        request,
      )}
    </Modal>
  );
};

export default QuickBooksModal;
